import React from 'react';
import { Box, Typography } from '@mui/material';
import { Science as ScienceIcon, Check as CheckIcon, WarningAmber as WarningAmberIcon, AutoAwesome as SparkleIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { colors } from '../../../themes/typography';
import { getExperimentStatusColor } from '../../../themes/experimentColors';
import SlotTooltip from './SlotTooltip';

const ALERT_COLOR = '#ef4444';

const EnhancedSlot = ({ slot, heatmapBg, isSelected, onInspect, onToggleSelect, cellSize, fontSize, metricFontSize = '11px', heatmapMode, heatmapValue, absoluteValue, isActMode, period = 30, categoryOverlay = false }) => {
  const { t } = useTranslation();
  const isLarge = cellSize >= 110;
  const [tooltip, setTooltip] = React.useState(null);
  const enterTimer = React.useRef(null);

  const handleMouseEnter = React.useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    enterTimer.current = setTimeout(() => {
      setTooltip({ x: rect.left + rect.width / 2, y: rect.top });
    }, 200);
  }, []);

  const handleMouseLeave = React.useCallback(() => {
    clearTimeout(enterTimer.current);
    setTooltip(null);
  }, []);

  if (!slot || !slot.name) {
    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.background.subtle,
        borderRadius: '8px',
        border: `1px solid ${colors.border.light}`,
        height: '100%',
        opacity: 0.4
      }}>
        <Typography sx={{ fontSize, color: colors.text.muted }}>-</Typography>
      </Box>
    );
  }

  const hasExperiment = Boolean(slot.experiment) && slot.experiment.status !== 'rejected';
  const hasSplitExperiments = Array.isArray(slot.experiments) && slot.experiments.length > 1;
  // Open technical alert (no sales for N weeks) with no experiment yet — the experiment pill wins otherwise
  const hasAlert = Boolean(slot.alert) && !hasExperiment;
  const tier = hasExperiment ? 'experiment' : (hasAlert ? 'alert' : 'normal');

  // Detect replacement/lane-expansion experiment where planogram still shows original product (swap not done)
  const needsSwapWarning = hasExperiment
    && ['reemplazo_producto', 'ampliar_carril'].includes(slot.experiment.experimentType)
    && slot.experiment.status === 'running'
    && slot.experiment.isSwapped === false;

  const borderColor = isSelected ? colors.primary.main : (categoryOverlay ? 'transparent' : colors.border.light);
  const borderWidth = isSelected ? 2 : 1;

  // Top-edge bar color: experiment status, else technical alert
  const topBarColor = hasExperiment ? getExperimentStatusColor(slot.experiment.status) : (hasAlert ? ALERT_COLOR : null);

  // Performance health indicator (traffic light): colored symbol
  const performanceIndicator = (() => {
    const delta = slot.salesDeltaPercentage;
    if (delta === null || delta === undefined) return null;
    if (delta > 5) return { color: colors.status.success, symbol: '▲' };
    if (delta > -15) return { color: colors.text.secondary, symbol: '—' };
    return { color: '#ef4444', symbol: '▼' };
  })();

  const isCategoryMode = heatmapMode === 'category';
  const isProductsMode = heatmapMode === 'products' || heatmapMode === 'none';

  const showCheckbox = isActMode || isSelected;

  // Shared checkbox component to avoid duplication
  const renderCheckbox = () => (
    <Box
      className="slot-checkbox"
      onClick={(e) => { e.stopPropagation(); onToggleSelect?.(slot); }}
      onKeyDown={(e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          onToggleSelect?.(slot);
        }
      }}
      tabIndex={0}
      role="checkbox"
      aria-checked={isSelected}
      aria-label={`${isSelected ? t('machines_deselect') : t('machines_select')} ${slot.name}`}
      sx={{
        flexShrink: 0,
        width: 14,
        height: 14,
        borderRadius: '3px',
        border: isSelected ? 'none' : `1.5px solid ${colors.border.medium}`,
        backgroundColor: isSelected ? colors.primary.main : 'rgba(255,255,255,0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        opacity: showCheckbox ? 1 : 0,
        transition: 'opacity 150ms ease',
        padding: '5px',
        margin: '-5px',
        boxSizing: 'content-box',
        '&:hover': {
          borderColor: colors.primary.main,
          backgroundColor: isSelected ? colors.primary.dark : colors.background.subtle
        },
        '&:focus-visible': {
          outline: `2px solid ${colors.primary.main}`,
          outlineOffset: '1px'
        }
      }}
    >
      {isSelected && <CheckIcon sx={{ fontSize: 10, color: 'white' }} />}
    </Box>
  );

  const pillSx = (color) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '1px',
    fontSize: '10px',
    fontWeight: 700,
    color,
    backgroundColor: `${color}18`,
    border: `1px solid ${color}30`,
    px: '3px',
    py: '0px',
    borderRadius: '3px',
    lineHeight: 1.2,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
  });

  // Unified status pill — experiment status, else technical alert; only color + text change
  const renderStatusPill = () => {
    if (hasAlert) {
      const alertLabel = t('machines_alertNoSalesShort', { weeks: slot.alert.weeksSince });
      return (
        <Box aria-label={`${t('machines_alertTechnical')}: ${alertLabel}`} sx={pillSx(ALERT_COLOR)}>
          <WarningAmberIcon sx={{ fontSize: 11, color: ALERT_COLOR }} />
          {alertLabel}
        </Box>
      );
    }
    if (!hasExperiment) return null;
    const status = slot.experiment.status;
    const statusColor = getExperimentStatusColor(status);
    const isManualReview = status === 'manual_review';
    const statusLabel = isManualReview ? t('machines_aiSuggestion') : t(status);
    const StatusIcon = isManualReview ? SparkleIcon : ScienceIcon;
    return (
      <Box
        aria-label={`${t('machines_experiment')}: ${statusLabel}${needsSwapWarning ? ` (${t('machines_swapPending', 'Swap pending')})` : ''}`}
        sx={pillSx(statusColor)}
      >
        <StatusIcon sx={{ fontSize: 11, color: statusColor }} />
        {statusLabel}
        {hasSplitExperiments && (
          <Box component="span" sx={{ fontSize: '9px', fontWeight: 800, ml: '2px' }}>×{slot.experiments.length}</Box>
        )}
        {needsSwapWarning && (
          <WarningAmberIcon sx={{ fontSize: 10, color: '#F59E0B' }} />
        )}
      </Box>
    );
  };

  // Format metrics for Products mode bottom row
  const salesLabel = slot.productAvgSales > 0 ? `${Number(slot.productAvgSales).toFixed(1)}u/d` : null;
  const marginLabel = slot.productMargin > 0 ? `€${Number(slot.productMargin).toFixed(2)}` : null;

  const renderContent = () => {
    if (isProductsMode) {
      // Products mode: name-centric with embedded metrics row
      return (
        <>
          {/* Header: checkbox + slot ID (right-aligned) + status pill */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
            {renderCheckbox()}
            <Typography sx={{
              fontSize: metricFontSize,
              fontWeight: 600,
              color: colors.text.muted,
              lineHeight: 1,
              marginLeft: 'auto'
            }}>
              {slot.selection_id || `${slot.row}-${slot.column}`}
            </Typography>
            {renderStatusPill()}
          </Box>

          {/* Center: product name (dominant) */}
          <Typography sx={{
            fontSize,
            fontWeight: 500,
            color: colors.text.primary,
            lineHeight: 1.25,
            textAlign: 'center',
            wordBreak: 'break-word',
            flex: 1,
            display: '-webkit-box',
            WebkitLineClamp: isLarge ? 2 : 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            alignContent: 'center'
          }}>
            {slot.name}
          </Typography>

          {/* Bottom: metrics row (sales, margin, trend) */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '2px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', overflow: 'hidden' }}>
              {salesLabel && (
                <Typography sx={{
                  fontSize: metricFontSize,
                  color: colors.text.secondary,
                  lineHeight: 1,
                  whiteSpace: 'nowrap'
                }}>
                  {salesLabel}
                </Typography>
              )}
              {marginLabel && (
                <Typography sx={{
                  fontSize: metricFontSize,
                  color: colors.text.secondary,
                  lineHeight: 1,
                  whiteSpace: 'nowrap'
                }}>
                  {marginLabel}
                </Typography>
              )}
            </Box>
            {performanceIndicator && (
              <Typography sx={{
                fontSize: isLarge ? '12px' : '11px',
                fontWeight: 700,
                color: performanceIndicator.color,
                lineHeight: 1,
                flexShrink: 0
              }}>
                {performanceIndicator.symbol}
              </Typography>
            )}
          </Box>
        </>
      );
    }

    // Category mode: name-first, colored background, no metrics
    return (
      <>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '2px' }}>
          {renderCheckbox()}
          <Typography sx={{
            fontSize: metricFontSize,
            fontWeight: 600,
            color: colors.text.muted,
            lineHeight: 1
          }}>
            {slot.selection_id || `${slot.row}-${slot.column}`}
          </Typography>
          {renderStatusPill()}
        </Box>

        <Typography sx={{
          fontSize,
          fontWeight: 500,
          color: colors.text.primary,
          lineHeight: 1.25,
          textAlign: 'center',
          wordBreak: 'break-word',
          flex: 1,
          display: '-webkit-box',
          WebkitLineClamp: isLarge ? 3 : 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          alignContent: 'center'
        }}>
          {slot.name}
        </Typography>

        <Box sx={{ height: 12 }} />
      </>
    );
  };

  return (
    <>
      {tooltip && (
        <Box sx={{
          position: 'fixed',
          left: tooltip.x,
          top: tooltip.y,
          transform: 'translate(-50%, -100%)',
          mt: '-8px',
          zIndex: 1500,
          pointerEvents: 'none',
          width: 'max-content',
          maxWidth: '280px',
        }}>
          <Box sx={{
            backgroundColor: 'rgba(30,30,30,0.95)',
            color: '#fff',
            borderRadius: '8px',
            px: 1.5,
            py: 1,
            fontSize: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}>
            <SlotTooltip slot={slot} period={period} />
          </Box>
        </Box>
      )}
      <Box
        onClick={() => onInspect?.(slot)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        p: isLarge ? '8px' : '6px',
        pt: topBarColor ? (isLarge ? '11px' : '9px') : undefined,
        height: '100%',
        backgroundColor: tier === 'experiment' && !heatmapBg
          ? `${getExperimentStatusColor(slot.experiment.status)}12`
          : tier === 'alert' && !heatmapBg
          ? `${ALERT_COLOR}12`
          : heatmapBg || colors.background.surface,
        border: `${borderWidth}px solid ${borderColor}`,
        borderRadius: categoryOverlay ? '4px' : '8px',
        cursor: 'pointer',
        transition: 'box-shadow 150ms ease-out',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: isSelected ? `0 0 0 1px ${colors.primary.main}` : 'none',
        '@media (prefers-reduced-motion: no-preference)': {
          transition: 'all 150ms ease-out'
        },
        '&:hover': {
          boxShadow: `0 2px 8px ${colors.shadow.soft}`,
          zIndex: 1,
          '& .slot-checkbox': { opacity: 1 }
        },
        '&:focus-visible': {
          outline: `2px solid ${colors.primary.main}`,
          outlineOffset: '2px'
        }
      }}
    >
      {/* Top-edge bar: experiment status color */}
      {topBarColor && (
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          backgroundColor: topBarColor,
          borderRadius: categoryOverlay ? '3px 3px 0 0' : '7px 7px 0 0',
          zIndex: 2,
        }} />
      )}

      {renderContent()}
    </Box>
    </>
  );
};

export default React.memo(EnhancedSlot);
